import { Outlet } from '@solidjs/router';
import { createEffect, createSignal, on, Show } from 'solid-js';
import { IntercomInitializer } from '~/components/common/IntercomInitializer';
import TrialLockout from '~/components/common/TrialLockout';
import { AutoPermissionGuard } from '~/components/permissions/AutoPermissionGuard';
import { useAuth, useTrialStatus } from '~/contexts/global';
import { useIsMobileWidth } from '~/hooks/useIsMobileWidth';
import { usePresenceHeartbeat } from '~/hooks/usePresenceHeartbeat';
import { Menu } from './Menu';
import { MenuProvider, useMenu } from './Menu/MenuContext';
import { Navigation } from './Navigation';

const useCobrowse = () => {
  const { me } = useAuth();
  createEffect(
    on(
      () => me()?.id,
      () => {
        if (!me()?.id) return;

        // Generate or retrieve device ID
        let deviceId = localStorage.getItem('device_id');
        if (!deviceId) {
          deviceId = crypto.randomUUID?.();
          localStorage.setItem('device_id', deviceId);
        }

        window.CobrowseIO.customData = {
          user_id: me()?.id,
          user_name: me()?.name,
          user_email: me()?.email,
          device_id: deviceId,
          device_name: 'PM - ' + me()?.name + ' - ' + me()?.email,
        };
      }
    )
  );
};

const Layout = () => {
  const [isSearchActive, setIsSearchActive] = createSignal<boolean>(false);

  useCobrowse();
  usePresenceHeartbeat();

  return (
    <MenuProvider>
      <LayoutContent isSearchActive={isSearchActive} setIsSearchActive={setIsSearchActive} />
    </MenuProvider>
  );
};

const LayoutContent = (props: { isSearchActive: () => boolean; setIsSearchActive: (value: boolean) => void }) => {
  const menu = useMenu();
  const { shouldLockoutUser } = useTrialStatus();
  const isMobileWidth = useIsMobileWidth();

  return (
    <Show when={!shouldLockoutUser()} fallback={<TrialLockout />}>
      <div class="h-dvh bg-white" classList={{ 'pt-14': isMobileWidth(), 'pt-16': !isMobileWidth() }}>
        <div
          id="layout-content"
          class="flex h-full flex-col overflow-auto"
          classList={{
            'overflow-x-hidden': menu?.isSidebarOpen() && menu?.isMobile(),
          }}>
          <div id="setting-layout-breadcrumb-container" class="sticky top-0 z-10 bg-white" />
          <Menu />
          <main
            class="flex w-full grow flex-col bg-grey"
            classList={{
              'md:pl-64': !menu?.isManuallyCollapsed(),
              'md:pl-16': menu?.isManuallyCollapsed(),
              'pl-[245px]': menu?.isSidebarOpen() && menu?.isMobile(),
            }}>
            <Navigation setIsSearchActive={props.setIsSearchActive} />

            <div class="flex-1">
              <AutoPermissionGuard>
                <Outlet />
              </AutoPermissionGuard>
            </div>
          </main>
          <Show when={props.isSearchActive()}>
            <div class="overlay fixed inset-0 z-40 bg-black/70" />
          </Show>
          <IntercomInitializer />
        </div>
      </div>
    </Show>
  );
};

export default Layout;
